/**
 * StubEditor — fallback editor for sections that don't yet have a custom UI.
 *
 * Rather than blocking admins behind "coming soon" we surface a JSON
 * textarea so any block can be edited today.  Every keystroke is parsed:
 * if the JSON is valid, the parent gets the new value through onChange;
 * if invalid, we hold the raw text locally and surface a parse error so
 * the user can fix it before publishing.
 *
 * As we build out a dedicated editor for each section (Services, FAQ,
 * Theme, …) we drop the corresponding tab from StubEditor's path and
 * route it to the new component in WebsiteSettings.  This file shouldn't
 * disappear though: it's a useful escape hatch for any free-form block
 * the schema may grow in the future.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Card, Button, Badge, ErrorBanner,
  COLOR, FONT, RADIUS,
} from '../../../components/ss';

// Local section header — the shared <Section> doesn't expose a
// description slot or a right-aligned action area, so we render our own.
function SectionBlock({ title, description, action, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', gap: 12, marginBottom: 10,
      }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: COLOR.text }}>{title}</div>
          {description && (
            <div style={{ fontSize: 12, color: COLOR.textMuted, marginTop: 3, lineHeight: 1.4 }}>
              {description}
            </div>
          )}
        </div>
        {action && <div style={{ flexShrink: 0 }}>{action}</div>}
      </div>
      {children}
    </div>
  );
}

// Sections we plan to ship dedicated editors for, in roughly the order
// we'll build them.  Surfaces a visible "Up next" hint so admins know
// the JSON view is temporary, not the long-term experience.
const ROADMAP_HINT = {
  brand:        'Logo, brand name, tagline.',
  nav:          'Top navigation links.',
  trustBar:     'Logos / badges shown beneath the hero.',
  about:        'About section copy + image.',
  features:     'Service cards (title, body, icon, link).',
  howItWorks:   'Step-by-step "how we work" band.',
  whyUs:        'Reasons-to-choose-us cards.',
  testimonials: 'Customer quotes.',
  serviceArea:  'Service-area map / list.',
  insurance:    'Accepted insurance plans / payors.',
  faq:          'FAQ accordion.',
  ctaBand:      'Final call-to-action band.',
  contact:      'Contact page copy + form labels.',
  footer:       'Footer columns + legal text.',
  theme:        'Brand colors & font families.',
  meta:         'SEO titles, descriptions, favicon.',
  ui:           'Global UI strings (form labels, fallbacks).',
  homeOrder:    'Order/visibility of bands on the home page.',
};

export default function StubEditor({ sectionKey, value, onChange }) {
  const [text, setText]   = useState(() => stringify(value));
  const [error, setError] = useState('');
  const lastExternal = useRef(stringify(value));

  // Keep local text in sync when the parent payload changes (e.g. after
  // Reload or a successful Publish).  We only overwrite when the parent
  // string actually differs from the last value we observed — otherwise
  // the user's own typing would clobber itself.
  useEffect(() => {
    const next = stringify(value);
    if (next !== lastExternal.current) {
      lastExternal.current = next;
      setText(next);
      setError('');
    }
  }, [value]);

  const handleChange = (e) => {
    const t = e.target.value;
    setText(t);
    if (t.trim() === '') {
      setError('');
      onChange(undefined);
      return;
    }
    try {
      const parsed = JSON.parse(t);
      setError('');
      onChange(parsed);
    } catch (err) {
      setError(err.message || 'Invalid JSON');
      // Don't propagate the broken value upward — keep last good in parent.
    }
  };

  const formatJson = () => {
    try {
      const parsed = JSON.parse(text);
      const pretty = JSON.stringify(parsed, null, 2);
      setText(pretty);
      setError('');
      onChange(parsed);
    } catch (err) {
      setError(err.message || 'Invalid JSON — fix errors before formatting.');
    }
  };

  const reset = () => {
    if (window.confirm(`Clear the "${sectionKey}" section? This sets it to an empty object.`)) {
      const empty = {};
      setText(stringify(empty));
      setError('');
      onChange(empty);
    }
  };

  const hint = ROADMAP_HINT[sectionKey];

  return (
    <Card>
      <SectionBlock
        title={`${sectionKey} — JSON editor`}
        description={
          <>
            <Badge color="amber" style={{ marginRight: 8 }}>Stub editor</Badge>
            A dedicated form for this section is coming soon. In the meantime
            you can edit the raw JSON below.{hint ? ` ${hint}` : ''}
          </>
        }
        action={
          <div style={{ display: 'flex', gap: 6 }}>
            <Button size="sm" variant="ghost" onClick={formatJson}>Format</Button>
            <Button size="sm" variant="ghost" onClick={reset}>Reset</Button>
          </div>
        }
      >
        {error && <ErrorBanner style={{ marginBottom: 10 }}>{error}</ErrorBanner>}
        <textarea
          value={text}
          onChange={handleChange}
          spellCheck={false}
          style={{
            width: '100%',
            minHeight: 360,
            fontFamily: FONT.mono || 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            fontSize: 12.5,
            lineHeight: 1.45,
            padding: '12px 14px',
            borderRadius: RADIUS.md,
            border: `1px solid ${error ? '#dc2626' : COLOR.grayBorder}`,
            background: '#0b1220',
            color: '#dbe7f5',
            resize: 'vertical',
            outline: 'none',
            tabSize: 2,
          }}
          onKeyDown={(e) => {
            // Tab inserts two spaces instead of changing focus.
            if (e.key === 'Tab') {
              e.preventDefault();
              const ta = e.target;
              const start = ta.selectionStart;
              const end   = ta.selectionEnd;
              const next  = text.slice(0, start) + '  ' + text.slice(end);
              setText(next);
              try {
                onChange(JSON.parse(next));
                setError('');
              } catch (err) {
                setError(err.message || 'Invalid JSON');
              }
              // Restore caret position after React updates the value.
              requestAnimationFrame(() => {
                ta.selectionStart = ta.selectionEnd = start + 2;
              });
            }
          }}
        />
        <div style={{ marginTop: 8, fontSize: 11, color: COLOR.textMuted }}>
          Edits are held in your draft until you click <b>Publish changes</b> at the top.
          Tab inserts two spaces. Use <b>Format</b> to pretty-print.
        </div>
      </SectionBlock>
    </Card>
  );
}

function stringify(value) {
  if (value === undefined || value === null) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
}
